import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import {
  BM_MODULE_NAMESPACE,
  BM_SET_LOADER_STATE,
  BM_LOADERS,
  BM_GET_BM_MEETINGS,
  BM_RESET_BM_MEETINGS,
  BM_MEETING_SET_VALUE_BY_FIELD,
  BM_ACTION,
  BM_EDIT_BM,
  BM_GET_BM,
  BM_DELETE_BM, BM_ARCHIVE_BM
} from '../../../store/building-monitor/bm-types.store'
import { IPagination } from '../../../models/generic'
import { IBMDetailViewModel, IBMMeetingListingViewModel } from '../../../models/bm'
import { ActivityStatus, CrudStatus } from '../../../common/enums'
import { IBMMeetingParameter } from '../../../models/bm/bm-meeting.parameter'
import { AUTHORIZATION_FUNCTIONALITIES, AUTHORIZATION_MODULE_NAMESPACE } from '../../../store/authorization'
import { APARTMENT_ACTION, APARTMENT_EDIT_APARTMENT } from '../../../store/apartment'
import { Uris } from '../../../common/constants'
const BMStore = namespace(BM_MODULE_NAMESPACE)
const AuthorizationStore = namespace(AUTHORIZATION_MODULE_NAMESPACE)

@Component({
  components: {
    'list-condition': async () => import('../components/bm-meeting-conditions/BMMeetingConditions.vue'),
    'list-item': async () => import('../components/bm-meeting-list-item/BMMeetingListItem.vue'),
    'bm-list-loader': async () => import('../loaders/bm-list-loader/BMListLoader.vue')
  }
})
class BMChildListContainer extends Vue {
  @AuthorizationStore.Action(AUTHORIZATION_FUNCTIONALITIES)
  storeGetFunctionalities: (moduleName: string) => Promise<any>;

  @BMStore.Getter activityStatus: ActivityStatus;
  @BMStore.Getter pagination: IPagination;
  @BMStore.Getter bmMeetingParameter: IBMMeetingParameter;
  @BMStore.Getter bmMeetings: {
    activityStatus: ActivityStatus,
    data: IBMMeetingListingViewModel[]
  };
  @BMStore.Getter bmCrudActionStatus: CrudStatus
  @BMStore.Getter bmSource: {
    activityStatus: ActivityStatus;
    detail: IBMDetailViewModel
  }

  @BMStore.Mutation(BM_MEETING_SET_VALUE_BY_FIELD)
  storeAssignValueForBMParameter: (value: { [x: string]: any }) => void;

  @BMStore.Action(BM_GET_BM_MEETINGS)
  storeGetBMMeetings: (payload: { param: IBMMeetingParameter, merge: boolean }) => Promise<any>;

  @BMStore.Mutation(BM_RESET_BM_MEETINGS)
  storeResetBMMeetings: () => void;

  @BMStore.Mutation(BM_SET_LOADER_STATE)
  storeSetLoaderState: (payload: { key: string, value: boolean }) => void

  @BMStore.Mutation(BM_ACTION)
  storeSetBMAction: (action: CrudStatus) => void

  @BMStore.Action(BM_EDIT_BM)
  storeEditBM: () => void

  @BMStore.Action(BM_DELETE_BM)
  storeDeleteBM: (payload: { bmid: string }) => Promise<any>;

  @BMStore.Action(BM_ARCHIVE_BM)
  storeArchiveBM: (payload: { bmid: string }) => Promise<any>;

  @BMStore.Action(BM_GET_BM)
  storeGetBMSource: (id: string) => Promise<any>

  dtError: any;
  dtMessageErrorModel: boolean;
  dtIsLoading: boolean;

  constructor() {
    super()
    this.dtMessageErrorModel = false
    this.dtIsLoading = false
  }

  mounted() {
    if (!this.bmSource.detail.id) {
      this.storeGetBMSource(this.$route.params.id).then()
    }
    this.loadFunctionality()
  }

  get lblSetting() {
    return {
      ...require('@/locales/building-monitor').default
    }
  }
  get bmMeetingsViewModel(): IBMMeetingListingViewModel[] {
    return this.bmMeetings.data
  }
  get coSearchParameter(): IBMMeetingParameter {
    return this.bmMeetingParameter
  }
  set coSearchParameter(value: IBMMeetingParameter) {
    this.storeAssignValueForBMParameter(value)
  }

  get searchParam() {
    return { param: this.coSearchParameter, merge: true }
  }

  get isOpenDeleteMessage(): boolean {
    return this.bmCrudActionStatus === CrudStatus.Delete ||
      this.bmCrudActionStatus === CrudStatus.Archive
  }

  get deleteModalText(): string {
    if (this.bmCrudActionStatus === CrudStatus.Delete) {
      return this.$t(this.lblSetting.bmDeleteBtn) + ' ' + this.bmSourceName
    } else if (this.bmCrudActionStatus === CrudStatus.Archive) {
      return (this.bmSource.detail.is_archived ? this.$t(this.lblSetting.bmUnarchiveBM) : this.$t(this.lblSetting.bmArchiveBM)) + ' ' + this.bmSourceName
    }
    return ''
  }

  get bmSourceName(): string {
    if (!this.bmSource.detail) return ''
    return 'SDC - ' + this.bmSource.detail.block.name + (this.bmSource.detail.apartment ? (' - ' + this.bmSource.detail.apartment.apartment_number) : '')
  }

  handleDeleteCancel() {
    this.storeSetBMAction(CrudStatus.NoAction)
  }

  handleDeleteOK() {
    this.dtIsLoading = true
    if (this.bmCrudActionStatus === CrudStatus.Delete) {
      this.storeDeleteBM({ bmid: this.$route.params.id })
        .then(() => {
          this.dtIsLoading = false
          this.$router.replace(Uris.BMBase)
        })
        .catch(err => {
          this.dtMessageErrorModel = true
          this.dtError = err
        })
        .finally(() => {
          this.storeSetBMAction(CrudStatus.NoAction)
        })
    } else if (this.bmCrudActionStatus === CrudStatus.Archive) {
      this.storeArchiveBM({ bmid: this.$route.params.id })
        .then(() => {
          this.dtIsLoading = false
          this.$router.replace(Uris.BMBase)
        })
        .catch(err => {
          this.dtMessageErrorModel = true
          this.dtError = err
        })
        .finally(() => {
          this.storeSetBMAction(CrudStatus.NoAction)
        })
    }
  }

  handleCloseErrorModal() {
    this.dtMessageErrorModel = false
    this.dtError = null
  }

  setListLoaderState(value: boolean) {
    this.storeSetLoaderState({ key: BM_LOADERS.LIST, value })
  }

  storeGetBMMeetingsWithId(payload: { param: IBMMeetingParameter, merge: boolean, bmid: string }) {
    payload.bmid = this.$route.params.id
    return this.storeGetBMMeetings(payload)
  }

  loadFunctionality() {
    this.storeGetFunctionalities(BM_MODULE_NAMESPACE)
      .then(response => {
        if (this.$route.meta) {
          Object.keys(response).forEach(key => { this.$route.meta[key] = true })
        }
      })
      .catch(error => console.log(error))
  }
}

export default BMChildListContainer



// WEBPACK FOOTER //
// ./src/containers/building-monitor/bm-meeting-child-list/index.ts